/**
 * Temporary opt-in Products/SKU fetch diagnostic.
 * Enable only with ?picksSkuDiag=1 (session-sticky via sessionStorage).
 * Booleans + reason codes + counts only — no PII/credentials.
 */

export const PICKS_SKU_DIAG_BUILD_ID = 'picks-sku-diag-v5-multichannel'
export const PICKS_SKU_DIAG_QUERY = 'picksSkuDiag'
const SESSION_KEY = 'wagamour_picks_sku_diag'
const MAX_EVENTS = 80

/** Compact shareable flash-capture fields (no PII). */
export type PicksSkuDiagCaptureSummary = {
  buildId: string
  loadedBuild: string
  hrefHost: string
  clientChannel: string | null
  firstAcceptedWriter: string | null
  firstAcceptedAtMs: number | null
  firstAcceptedWithSku: number | null
  firstAcceptedTotalSku: number | null
  firstAcceptedIsOnline: boolean | null
  firstAcceptedCacheReason: string | null
  firstIconPaintAtMs: number | null
  firstIconPaintSource: string | null
  firstIconCards: number | null
  firstIconWithSku: number | null
  firstIconTotalSku: number | null
  firstSkuPaintAtMs: number | null
  firstSkuWithSku: number | null
  firstSkuTotalSku: number | null
  sawIconsBeforeSku: boolean | null
  firstRequestStartMs: number | null
  firstHttpMs: number | null
  firstHttpStatus: number | null
  requestToHttpMs: number | null
  hydrationMs: number | null
  assortmentRequestToSkuPaintMs: number | null
  firstImageLoadMs: number | null
  lastAcceptedWriter: string | null
  eventCount: number
}


/** Classify Products client channel for multi-platform flash capture. */
export function detectClientChannel(): string {
  if (typeof window === 'undefined') return 'ssr'
  try {
    const cap = (window as unknown as {
      Capacitor?: {
        isNativePlatform?: () => boolean
        getPlatform?: () => string
      }
    }).Capacitor
    const ua =
      typeof navigator !== 'undefined' && typeof navigator.userAgent === 'string'
        ? navigator.userAgent
        : ''
    const native =
      (typeof cap?.isNativePlatform === 'function' && cap.isNativePlatform()) ||
      ua.includes('WagamourCapacitorShell')
    const platform = cap?.getPlatform?.() || ''
    if (native && platform === 'ios') return 'ios_native'
    if (native && platform === 'android') return 'android_native'
    if (native && /iPhone|iPad|iPod/i.test(ua) && platform !== 'android') {
      return 'ios_native'
    }
    if (native) return 'android_native'
    const nav = navigator as Navigator & { standalone?: boolean }
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      nav.standalone === true
    if (standalone) return 'pwa_standalone'
  } catch {
    /* ignore */
  }
  return 'web_tab'
}

/** Best-effort page chunk + revision fingerprint from loaded DOM (no inference). */
export function detectLoadedAppBuild(): string {
  if (typeof document === 'undefined') return 'ssr'
  try {
    const rev =
      document
        .querySelector('[data-preview-cloud-run-revision]')
        ?.getAttribute('data-preview-cloud-run-revision') || ''
    const scripts = Array.from(document.getElementsByTagName('script'))
    let page = ''
    let webpack = ''
    for (const s of scripts) {
      const src = s.getAttribute('src') || ''
      if (!page) {
        const m = src.match(/\/(page-[a-f0-9]+\.js)/)
        if (m) page = m[1]
      }
      if (!webpack) {
        const w = src.match(/\/(webpack-[a-f0-9]+\.js)/)
        if (w) webpack = w[1]
      }
    }
    if (!page) {
      const html = document.documentElement?.innerHTML || ''
      const m2 = html.match(/\/(page-[a-f0-9]+\.js)/)
      if (m2) page = m2[1]
    }
    const parts = [rev || null, page || null, webpack || null].filter(Boolean)
    if (parts.length) return parts.join('|')
  } catch {
    /* ignore */
  }
  return 'unknown'
}

/** Live shell/upgrade probe for on-screen diag (installed PWA). */
export function sampleShellUpgradeDiagFlags(): Record<
  string,
  PicksSkuDiagFlagValue
> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return { sample: 'ssr' }
  }
  const shell = document.querySelector<HTMLElement>('.app-beta-inner-shell')
  const logo = document.querySelector<HTMLElement>(
    '.app-beta-inner-shell img[src*="wagamour-logo"]'
  )
  const cs = shell ? window.getComputedStyle(shell) : null
  const shellRect = shell?.getBoundingClientRect()
  const logoRect = logo?.getBoundingClientRect()
  const nav = navigator as Navigator & { standalone?: boolean }
  const statusMeta =
    document
      .querySelector('meta[name="apple-mobile-web-app-status-bar-style"]')
      ?.getAttribute('content') || ''
  const modalOpen = Boolean(
    document.querySelector(
      '[role="dialog"][aria-modal="true"], [data-testid="upgrade-modal-open"]'
    )
  )
  return {
    loadedBuild: detectLoadedAppBuild(),
    displayStandalone: window.matchMedia('(display-mode: standalone)').matches,
    navStandalone: nav.standalone === true,
    statusBarMeta: statusMeta.slice(0, 24),
    shellPadAttr: (shell?.getAttribute('data-shell-top-pad') || '').slice(0, 40),
    shellPadComputed: (cs?.paddingTop || '').slice(0, 24),
    shellTop: shellRect ? Math.round(shellRect.top) : -1,
    logoTop: logoRect ? Math.round(logoRect.top) : -1,
    scrollY: Math.round(
      window.scrollY ||
        document.documentElement.scrollTop ||
        document.body.scrollTop ||
        0
    ),
    bodyPosition: document.body.style.position || 'none',
    bodyTop: (document.body.style.top || '').slice(0, 16),
    modalOpen,
  }
}

export type PicksSkuDiagPath = 'picks' | 'pills' | 'agentic' | 'none'

export type PicksSkuDiagFlagValue = boolean | number | string

export type PicksSkuDiagEvent = {
  /** ms since diag enabled */
  t: number
  kind:
    | 'startup'
    | 'effect'
    | 'early_return'
    | 'decision'
    | 'request'
    | 'response'
    | 'flag'
  /** reason / step code */
  code: string
  path?: PicksSkuDiagPath
  flags?: Record<string, PicksSkuDiagFlagValue>
}

export type PicksSkuDiagSnapshot = {
  enabled: boolean
  buildId: string
  /** Observed page-*.js from document scripts (not inferred from Cloud Run). */
  loadedBuild: string
  /** Last writer that accepted a picks paint (cache_restore / network / …). */
  lastAcceptedWriter: string | null
  startedAt: number | null
  effectRan: boolean
  lastEarlyReturn: string | null
  selectedPath: PicksSkuDiagPath
  events: PicksSkuDiagEvent[]
  /** Derived flash-capture fields for on-device share (no console). */
  capture: PicksSkuDiagCaptureSummary
  /** Plain-text dump for Copy / Share. */
  captureDump: string
}

type Listener = () => void

let enabledCache: boolean | null = null
let startedAt: number | null = null
let effectRan = false
let lastEarlyReturn: string | null = null
let selectedPath: PicksSkuDiagPath = 'none'
let lastAcceptedWriter: string | null = null
let loadedBuildCache: string | null = null
/** First DOM paint of product/icon cards (observed; not inferred). */
let firstImageLoadMs: number | null = null
let firstDomIconPaint: {
  t: number
  cards: number
  skuRails: number
} | null = null
const events: PicksSkuDiagEvent[] = []
const listeners = new Set<Listener>()

function notify(): void {
  for (const l of listeners) {
    try {
      l()
    } catch {
      /* ignore */
    }
  }
}

function readQueryEnabled(): boolean {
  if (typeof window === 'undefined') return false
  try {
    const q = new URLSearchParams(window.location.search)
    if (q.get(PICKS_SKU_DIAG_QUERY) === '1') {
      sessionStorage.setItem(SESSION_KEY, '1')
      return true
    }
    return sessionStorage.getItem(SESSION_KEY) === '1'
  } catch {
    return false
  }
}

/** Off by default; on only after ?picksSkuDiag=1 (sticky for the tab session). */
export function isPicksSkuDiagEnabled(): boolean {
  if (typeof window === 'undefined') return false
  if (enabledCache != null) return enabledCache
  enabledCache = readQueryEnabled()
  if (enabledCache && startedAt == null) {
    startedAt = Date.now()
  }
  return enabledCache
}

/** Call once at app shell mount so enable + startup event happen early. */
export function bootstrapPicksSkuDiag(): boolean {
  enabledCache = null
  const on = isPicksSkuDiagEnabled()
  if (!on) return false
  if (startedAt == null) startedAt = Date.now()
  loadedBuildCache = detectLoadedAppBuild()
  recordPicksSkuDiag({
    kind: 'startup',
    code: 'app_startup',
    path: 'none',
    flags: {
      buildId: PICKS_SKU_DIAG_BUILD_ID,
      loadedBuild: loadedBuildCache,
      queryParam: true,
      clientChannel: detectClientChannel(),
    },
  })
  try {
    const w = window as unknown as {
      __getPicksSkuDiagSnapshot?: typeof getPicksSkuDiagSnapshot
      __getPicksSkuDiagCaptureDump?: () => string
    }
    w.__getPicksSkuDiagSnapshot = getPicksSkuDiagSnapshot
    w.__getPicksSkuDiagCaptureDump = () => formatPicksSkuDiagCaptureDump()
  } catch {
    /* ignore */
  }
  return true
}

/** Record which writer accepted a Products payload paint. */
export function recordAcceptedPicksWriter(
  writer: string,
  flags?: Record<string, PicksSkuDiagFlagValue>
): void {
  const w = String(writer || 'unknown').slice(0, 40)
  lastAcceptedWriter = w
  recordPicksSkuDiag({
    kind: 'flag',
    code: 'accepted_writer',
    path: 'picks',
    flags: { writer: w, ...(flags || {}) },
  })
}

/**
 * Observe first product/icon card paint in the DOM.
 * Call from overlay while diag is enabled. Records once.
 */
export function noteDomProductPaint(input: {
  cards: number
  skuRails: number
}): void {
  if (!isPicksSkuDiagEnabled()) return
  if (firstDomIconPaint) return
  const cards = Math.max(0, Number(input.cards) || 0)
  if (cards <= 0) return
  if (startedAt == null) startedAt = Date.now()
  const t = Date.now() - startedAt
  const skuRails = Math.max(0, Number(input.skuRails) || 0)
  firstDomIconPaint = { t, cards, skuRails }
  recordPicksSkuDiag({
    kind: 'flag',
    code: 'dom_first_icon_paint',
    path: 'picks',
    flags: {
      cards,
      skuRails,
      withSkuLikely: skuRails > 0,
    },
  })
}


/** Record first SKU product image load (onload), once. */
export function noteFirstSkuImageLoad(): void {
  if (!isPicksSkuDiagEnabled()) return
  if (firstImageLoadMs != null) return
  if (startedAt == null) startedAt = Date.now()
  firstImageLoadMs = Date.now() - startedAt
  recordPicksSkuDiag({
    kind: 'flag',
    code: 'first_sku_image_load',
    path: 'picks',
    flags: { firstImageLoadMs },
  })
}

function numFlag(
  flags: Record<string, PicksSkuDiagFlagValue> | undefined,
  key: string
): number | null {
  const v = flags?.[key]
  return typeof v === 'number' && Number.isFinite(v) ? v : null
}

function boolFlag(
  flags: Record<string, PicksSkuDiagFlagValue> | undefined,
  key: string
): boolean | null {
  const v = flags?.[key]
  return typeof v === 'boolean' ? v : null
}

function strFlag(
  flags: Record<string, PicksSkuDiagFlagValue> | undefined,
  key: string
): string | null {
  const v = flags?.[key]
  return typeof v === 'string' ? v.slice(0, 64) : null
}

/** Derive shareable flash-capture summary from recorded events + DOM note. */
export function buildPicksSkuDiagCaptureSummary(): PicksSkuDiagCaptureSummary {
  const hrefHost =
    typeof window !== 'undefined' ? window.location.hostname || 'unknown' : 'ssr'

  let firstAccepted: PicksSkuDiagEvent | null = null
  let firstIconPayload: PicksSkuDiagEvent | null = null
  let firstSkuPayload: PicksSkuDiagEvent | null = null
  let firstRequest: PicksSkuDiagEvent | null = null
  let firstHttp: PicksSkuDiagEvent | null = null

  for (const e of events) {
    if (!firstAccepted && e.code === 'accepted_writer') firstAccepted = e
    if (
      !firstIconPayload &&
      e.code === 'rendered_picks_payload' &&
      (numFlag(e.flags, 'bundleCount') || 0) > 0 &&
      (numFlag(e.flags, 'totalSku') || 0) === 0 &&
      // Empty hydration shell has bundles but no parent cards — not an icon flash.
      boolFlag(e.flags, 'hydrationShell') !== true &&
      (numFlag(e.flags, 'parentCards') || 0) > 0
    ) {
      firstIconPayload = e
    }
    if (
      !firstSkuPayload &&
      e.code === 'rendered_picks_payload' &&
      (numFlag(e.flags, 'totalSku') || 0) > 0
    ) {
      firstSkuPayload = e
    }
    if (!firstRequest && e.code === 'picks_request_start') firstRequest = e
    if (!firstHttp && e.code === 'picks_http') firstHttp = e
  }

  const iconAtCandidates: Array<{
    t: number
    source: string
    cards: number | null
    withSku: number | null
    totalSku: number | null
  }> = []
  // DOM paint with SKU rails already present is SKU paint, not a pre-SKU icon flash.
  if (firstDomIconPaint && firstDomIconPaint.skuRails === 0) {
    iconAtCandidates.push({
      t: firstDomIconPaint.t,
      source: 'dom_first_icon_paint',
      cards: firstDomIconPaint.cards,
      withSku: 0,
      totalSku: null,
    })
  }
  if (firstIconPayload) {
    iconAtCandidates.push({
      t: firstIconPayload.t,
      source: 'rendered_picks_payload_zero_sku',
      cards: numFlag(firstIconPayload.flags, 'bundleCount'),
      withSku: numFlag(firstIconPayload.flags, 'withSku'),
      totalSku: numFlag(firstIconPayload.flags, 'totalSku'),
    })
  }
  // Accepted writer with withSku=0 also counts as icon-first paint signal
  // (exclude loading shell holds — those are skeleton, not category icons).
  const acceptedWriter = firstAccepted
    ? strFlag(firstAccepted.flags, 'writer') || ''
    : ''
  const acceptedPaint = firstAccepted
    ? strFlag(firstAccepted.flags, 'paintSource') || ''
    : ''
  if (
    firstAccepted &&
    numFlag(firstAccepted.flags, 'withSku') === 0 &&
    (numFlag(firstAccepted.flags, 'bundleCount') || 0) > 0 &&
    !acceptedWriter.includes('shell_hold') &&
    acceptedPaint !== 'shell'
  ) {
    iconAtCandidates.push({
      t: firstAccepted.t,
      source: 'accepted_writer_zero_sku',
      cards: numFlag(firstAccepted.flags, 'bundleCount'),
      withSku: 0,
      totalSku: numFlag(firstAccepted.flags, 'totalSku'),
    })
  }
  iconAtCandidates.sort((a, b) => a.t - b.t)
  const firstIcon = iconAtCandidates[0] || null

  const firstSkuAt = firstSkuPayload?.t ?? null
  let sawIconsBeforeSku: boolean | null = null
  if (firstIcon && firstSkuAt != null) {
    // Same-ms events: treat zero-sku payload / accepted icon writer as before SKU.
    const iconImpliesBefore =
      firstIcon.source === 'rendered_picks_payload_zero_sku' ||
      firstIcon.source === 'accepted_writer_zero_sku' ||
      (firstIcon.source === 'dom_first_icon_paint' &&
        (firstIcon.withSku || 0) === 0)
    sawIconsBeforeSku =
      firstIcon.t < firstSkuAt ||
      (firstIcon.t === firstSkuAt && iconImpliesBefore)
  } else if (firstIcon && firstSkuAt == null) {
    sawIconsBeforeSku = true
  } else if (!firstIcon && firstSkuAt != null) {
    sawIconsBeforeSku = false
  }

  const reqStart = firstRequest?.t ?? null
  const httpMs = firstHttp?.t ?? null

  return {
    buildId: PICKS_SKU_DIAG_BUILD_ID,
    loadedBuild: loadedBuildCache || detectLoadedAppBuild(),
    hrefHost,
    clientChannel: detectClientChannel(),
    firstAcceptedWriter: firstAccepted
      ? strFlag(firstAccepted.flags, 'writer') || lastAcceptedWriter
      : null,
    firstAcceptedAtMs: firstAccepted?.t ?? null,
    firstAcceptedWithSku: firstAccepted
      ? numFlag(firstAccepted.flags, 'withSku')
      : null,
    firstAcceptedTotalSku: firstAccepted
      ? numFlag(firstAccepted.flags, 'totalSku')
      : null,
    firstAcceptedIsOnline: firstAccepted
      ? boolFlag(firstAccepted.flags, 'isOnline')
      : null,
    firstAcceptedCacheReason: firstAccepted
      ? strFlag(firstAccepted.flags, 'cacheReason')
      : null,
    firstIconPaintAtMs: firstIcon?.t ?? null,
    firstIconPaintSource: firstIcon?.source ?? null,
    firstIconCards: firstIcon?.cards ?? null,
    firstIconWithSku: firstIcon?.withSku ?? null,
    firstIconTotalSku: firstIcon?.totalSku ?? null,
    firstSkuPaintAtMs: firstSkuAt,
    firstSkuWithSku: firstSkuPayload
      ? numFlag(firstSkuPayload.flags, 'withSku')
      : null,
    firstSkuTotalSku: firstSkuPayload
      ? numFlag(firstSkuPayload.flags, 'totalSku')
      : null,
    sawIconsBeforeSku,
    firstRequestStartMs: reqStart,
    firstHttpMs: httpMs,
    firstHttpStatus: firstHttp ? numFlag(firstHttp.flags, 'httpStatus') : null,
    requestToHttpMs:
      reqStart != null && httpMs != null ? Math.max(0, httpMs - reqStart) : null,
    hydrationMs: (() => {
      for (const e of events) {
        if (String(e.code || '').startsWith('hydrate_')) {
          const n = numFlag(e.flags, 'hydrationMs')
          if (n != null) return n
        }
      }
      return null
    })(),
    assortmentRequestToSkuPaintMs:
      reqStart != null && firstSkuAt != null
        ? Math.max(0, firstSkuAt - reqStart)
        : null,
    firstImageLoadMs,
    lastAcceptedWriter,
    eventCount: events.length,
  }
}

export function formatPicksSkuDiagCaptureDump(
  summary?: PicksSkuDiagCaptureSummary
): string {
  const s = summary || buildPicksSkuDiagCaptureSummary()
  const lines = [
    '=== picksSkuDiag flash capture ===',
    `buildId=${s.buildId}`,
    `loadedBuild=${s.loadedBuild}`,
    `host=${s.hrefHost}`,
    `clientChannel=${s.clientChannel ?? 'n/a'}`,
    `firstAcceptedWriter=${s.firstAcceptedWriter ?? 'none'}`,
    `firstAcceptedAtMs=${s.firstAcceptedAtMs ?? 'n/a'}`,
    `firstAcceptedWithSku=${s.firstAcceptedWithSku ?? 'n/a'}`,
    `firstAcceptedTotalSku=${s.firstAcceptedTotalSku ?? 'n/a'}`,
    `firstAcceptedIsOnline=${s.firstAcceptedIsOnline ?? 'n/a'}`,
    `firstAcceptedCacheReason=${s.firstAcceptedCacheReason ?? 'n/a'}`,
    `firstIconPaintAtMs=${s.firstIconPaintAtMs ?? 'n/a'}`,
    `firstIconPaintSource=${s.firstIconPaintSource ?? 'n/a'}`,
    `firstIconCards=${s.firstIconCards ?? 'n/a'}`,
    `firstIconWithSku=${s.firstIconWithSku ?? 'n/a'}`,
    `firstIconTotalSku=${s.firstIconTotalSku ?? 'n/a'}`,
    `firstSkuPaintAtMs=${s.firstSkuPaintAtMs ?? 'n/a'}`,
    `firstSkuWithSku=${s.firstSkuWithSku ?? 'n/a'}`,
    `firstSkuTotalSku=${s.firstSkuTotalSku ?? 'n/a'}`,
    `sawIconsBeforeSku=${s.sawIconsBeforeSku ?? 'n/a'}`,
    `firstRequestStartMs=${s.firstRequestStartMs ?? 'n/a'}`,
    `firstHttpMs=${s.firstHttpMs ?? 'n/a'}`,
    `firstHttpStatus=${s.firstHttpStatus ?? 'n/a'}`,
    `requestToHttpMs=${s.requestToHttpMs ?? 'n/a'}`,
    `hydrationMs=${s.hydrationMs ?? 'n/a'}`,
    `assortmentRequestToSkuPaintMs=${s.assortmentRequestToSkuPaintMs ?? 'n/a'}`,
    `firstImageLoadMs=${s.firstImageLoadMs ?? 'n/a'}`,
    `lastAcceptedWriter=${s.lastAcceptedWriter ?? 'none'}`,
    `eventCount=${s.eventCount}`,
    '--- recent events ---',
  ]
  for (const e of events.slice(-20)) {
    const flags = e.flags
      ? ' ' +
        Object.entries(e.flags)
          .map(([k, v]) => `${k}=${String(v)}`)
          .join(' ')
      : ''
    lines.push(`+${e.t}ms ${e.kind}/${e.code}${flags}`)
  }
  return lines.join('\n')
}

export function subscribePicksSkuDiag(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function sanitizeFlags(
  flags?: Record<string, PicksSkuDiagFlagValue>
): Record<string, PicksSkuDiagFlagValue> | undefined {
  if (!flags) return undefined
  const out: Record<string, PicksSkuDiagFlagValue> = {}
  for (const [k, v] of Object.entries(flags)) {
    if (typeof v === 'boolean' || typeof v === 'number') {
      out[k] = v
      continue
    }
    if (typeof v === 'string') {
      // Reason codes / enums only — reject anything that looks like an id/token/email
      if (
        v.length > 64 ||
        v.includes('@') ||
        /^(cm|user|dog)[a-z0-9]{8,}$/i.test(v) ||
        /bearer|token|secret|password/i.test(v)
      ) {
        continue
      }
      out[k] = v
    }
  }
  return out
}

export function recordPicksSkuDiag(input: {
  kind: PicksSkuDiagEvent['kind']
  code: string
  path?: PicksSkuDiagPath
  flags?: Record<string, PicksSkuDiagFlagValue>
}): void {
  if (!isPicksSkuDiagEnabled()) return
  if (startedAt == null) startedAt = Date.now()

  if (input.kind === 'effect' && input.code === 'fetch_effect_run') {
    effectRan = true
  }
  if (input.kind === 'early_return') {
    lastEarlyReturn = input.code
  }
  if (input.path) {
    selectedPath = input.path
  }

  const event: PicksSkuDiagEvent = {
    t: Date.now() - (startedAt || Date.now()),
    kind: input.kind,
    code: String(input.code).slice(0, 80),
    ...(input.path ? { path: input.path } : {}),
    ...(sanitizeFlags(input.flags)
      ? { flags: sanitizeFlags(input.flags) }
      : {}),
  }
  events.push(event)
  while (events.length > MAX_EVENTS) events.shift()
  notify()
}

export function getPicksSkuDiagSnapshot(): PicksSkuDiagSnapshot {
  if (loadedBuildCache == null && typeof document !== 'undefined') {
    loadedBuildCache = detectLoadedAppBuild()
  }
  const capture = buildPicksSkuDiagCaptureSummary()
  return {
    enabled: isPicksSkuDiagEnabled(),
    buildId: PICKS_SKU_DIAG_BUILD_ID,
    loadedBuild: loadedBuildCache || 'unknown',
    lastAcceptedWriter,
    startedAt,
    effectRan,
    lastEarlyReturn,
    selectedPath,
    events: events.slice(),
    capture,
    captureDump: formatPicksSkuDiagCaptureDump(capture),
  }
}

/** Test helper — reset module state. */
export function resetPicksSkuDiagForTests(): void {
  enabledCache = null
  startedAt = null
  effectRan = false
  lastEarlyReturn = null
  selectedPath = 'none'
  lastAcceptedWriter = null
  loadedBuildCache = null
  firstDomIconPaint = null
  firstImageLoadMs = null
  events.length = 0
  listeners.clear()
  if (typeof window !== 'undefined') {
    try {
      sessionStorage.removeItem(SESSION_KEY)
    } catch {
      /* ignore */
    }
  }
}

export function countSkuCarouselsByBundle(payload: unknown): {
  bundleCount: number
  withSku: number
  cacheOk: boolean | null
  totalSku: number
} {
  if (!payload || typeof payload !== 'object') {
    return { bundleCount: 0, withSku: 0, cacheOk: null, totalSku: 0 }
  }
  const p = payload as {
    sections?: unknown[]
    skuCacheEnrichment?: { cacheOk?: boolean }
  }
  const sections = Array.isArray(p.sections) ? p.sections : []
  let withSku = 0
  let totalSku = 0
  for (const section of sections) {
    if (!section || typeof section !== 'object') continue
    const s = section as { picks?: unknown[]; items?: unknown[] }
    const picks = Array.isArray(s.picks) ? s.picks : []
    const items = Array.isArray(s.items) ? s.items : []
    // Count SKUs from either array so lagging picks/items cannot hide enrichment.
    const list = [...picks, ...items]
    const seen = new Set<string>()
    let sectionSku = 0
    for (const item of list) {
      if (!item || typeof item !== 'object') continue
      const c = (item as { skuCarousel?: unknown; id?: unknown }).skuCarousel
      if (!Array.isArray(c) || c.length === 0) continue
      const id = String((item as { id?: unknown }).id ?? '')
      const key = id || JSON.stringify(c.map((x) => (x as { catalogItemId?: unknown })?.catalogItemId))
      if (seen.has(key)) continue
      seen.add(key)
      sectionSku += c.length
    }
    if (sectionSku > 0) withSku += 1
    totalSku += sectionSku
  }
  const cacheOk =
    p.skuCacheEnrichment && typeof p.skuCacheEnrichment === 'object'
      ? Boolean(p.skuCacheEnrichment.cacheOk)
      : null
  return {
    bundleCount: sections.length,
    withSku,
    cacheOk,
    totalSku,
  }
}
